<template>
  <div class="login-page">
    <!-- Left Hero Panel -->
    <div class="login-hero">
      <img
        src="/login-hero.png"
        alt="BusinessPe"
        class="hero-img"
        onerror="this.style.display='none'"
      />
      <div class="hero-overlay"></div>
      <div class="hero-content">
        <div class="hero-logo">
          <span class="hero-logo-icon">B</span>
        </div>
        <h2 class="hero-title">
          Grow Your Business<br />with <span class="hero-brand">BusinessPe</span>
        </h2>
        <p class="hero-subtitle">
          Manage investments, products, RMC plants, and operations —
          all from a single powerful platform.
        </p>
        <div class="hero-stats">
          <div class="hero-stat">
            <span class="hero-stat-value">500+</span>
            <span class="hero-stat-label">Companies</span>
          </div>
          <div class="hero-stat-divider"></div>
          <div class="hero-stat">
            <span class="hero-stat-value">10K+</span>
            <span class="hero-stat-label">Users</span>
          </div>
          <div class="hero-stat-divider"></div>
          <div class="hero-stat">
            <span class="hero-stat-value">99.9%</span>
            <span class="hero-stat-label">Uptime</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Right Form Panel -->
    <div class="login-form-panel">
      <div class="login-form-inner">
        <!-- Logo for mobile -->
        <div class="mobile-logo">
          <img
            :src="is_company_user ? `${mediaUrl + logo_path}` : require('../../assets/BusinessPe.png')"
            alt="BusinessPe"
            class="mobile-logo-img"
          />
        </div>

        <!-- Heading -->
        <div class="form-heading">
          <h1 class="form-title">
            Sign in to <span class="form-brand">BusinessPe</span>
          </h1>
          <p class="form-subtitle">
            {{ is_company_user ? "Welcome back! Please enter your details." : "All Your Business Needs in One Place." }}
          </p>
        </div>

        <!-- Form -->
        <div class="login-form">
          <div class="field-group">
            <label class="field-label">Email Address</label>
            <v-text-field
              v-model="form.email"
              outlined
              dense
              placeholder="Enter your email"
              prepend-inner-icon="mdi-email-outline"
              :error-messages="errors.email"
              hide-details="auto"
              class="modern-field"
              @click:clear="form.email = null"
              clearable
            ></v-text-field>
          </div>

          <div class="field-group">
            <div class="field-label-row">
              <label class="field-label">Password</label>
              <router-link to="/forgot-password" class="forgot-link">
                Forgot password?
              </router-link>
            </div>
            <v-text-field
              v-model="form.password"
              :append-icon="showPassword ? 'mdi-eye-outline' : 'mdi-eye-off-outline'"
              :type="showPassword ? 'text' : 'password'"
              outlined
              dense
              placeholder="Enter your password"
              prepend-inner-icon="mdi-lock-outline"
              :error-messages="errors.password"
              hide-details="auto"
              class="modern-field"
              @keypress.enter="login"
              @click:append="showPassword = !showPassword"
            ></v-text-field>
          </div>

          <v-btn
            :loading="isLoading"
            :disabled="isLoading"
            block
            depressed
            large
            class="login-btn"
            @click="login"
          >
            <v-icon left small>mdi-login</v-icon>
            Sign In
          </v-btn>
        </div>

        <!-- Footer -->
        <p class="login-footer">
          BusinessPe Admin Portal &middot; v1.0
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import axios from "axios";

export default {
  name: "LoginPage",
  data() {
    return {
      form: {
        email: "",
        password: "",
        portal: true,
      },
      logo_path: "",
      is_company_user: false,
      showPassword: false,
      isLoading: false,
    };
  },
  mounted() {
    const companyId = localStorage.getItem("company-id");
    if (companyId && companyId.length) {
      this.getCompany(companyId);
    }
  },
  methods: {
    async getCompany(companyId) {
      await axios
        .get(`/get_company/${this.encryptIt(companyId)}`)
        .then((company) => {
          company = company.data.data;
          this.logo_path = company.logo_path;
        })
        .catch(() => {
          this.logo_path = "";
        });
    },
    ...mapActions({
      logIn: "auth/logIn",
    }),
    async login() {
      this.isLoading = true;
      this.logIn(this.form)
        .then(() => {
          this.isLoading = false;
          location.reload("/");
        })
        .catch(() => {
          alert("User can't login in portal");
          this.isLoading = false;
        });
    },
  },
};
</script>

<style scoped>
.login-page {
  display: flex;
  min-height: 100vh;
  background: #fff;
}

/* ── Left Hero ── */
.login-hero {
  display: none;
  position: relative;
  overflow: hidden;
  background: #0f1b2d;
  flex-direction: column;
  justify-content: flex-end;
}

@media (min-width: 1024px) {
  .login-hero {
    display: flex;
    width: 62%;
  }
}

.hero-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.hero-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom,
    rgba(15, 27, 45, 0.3) 0%,
    rgba(15, 27, 45, 0.85) 100%
  );
}

.hero-content {
  position: relative;
  padding: 48px 56px;
  z-index: 1;
}

.hero-logo {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: linear-gradient(135deg, #1A3A6B, #152E56);
  box-shadow: 0 4px 16px rgba(26, 58, 107, 0.5);
  margin-bottom: 24px;
}

.hero-logo-icon {
  font-size: 22px;
  font-weight: 700;
  color: #fff;
}

.hero-title {
  font-size: 28px;
  font-weight: 700;
  color: #fff;
  line-height: 1.3;
  margin-bottom: 12px;
}

.hero-brand {
  color: #60a5fa;
}

.hero-subtitle {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.65);
  line-height: 1.7;
  max-width: 480px;
  margin-bottom: 32px;
}

.hero-stats {
  display: flex;
  align-items: center;
  gap: 24px;
}

.hero-stat {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.hero-stat-value {
  font-size: 20px;
  font-weight: 700;
  color: #fff;
}

.hero-stat-label {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.55);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.hero-stat-divider {
  width: 1px;
  height: 36px;
  background: rgba(255, 255, 255, 0.2);
}

/* ── Right Form Panel ── */
.login-form-panel {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 40px 24px;
}

@media (min-width: 1024px) {
  .login-form-panel {
    width: 38%;
    padding: 48px 56px;
  }
}

.login-form-inner {
  width: 100%;
  max-width: 400px;
}

/* Mobile logo */
.mobile-logo {
  display: flex;
  justify-content: center;
  margin-bottom: 28px;
}

@media (min-width: 1024px) {
  .mobile-logo {
    display: none;
  }
}

.mobile-logo-img {
  height: 56px;
  object-fit: contain;
}

/* Heading */
.form-heading {
  margin-bottom: 32px;
}

.form-title {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.2;
  margin-bottom: 6px;
}

.form-brand {
  color: #1A3A6B;
}

.form-subtitle {
  font-size: 14px;
  color: #64748b;
}

/* Form */
.login-form {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.field-label {
  font-size: 13px;
  font-weight: 500;
  color: #374151;
}

.forgot-link {
  font-size: 12px;
  font-weight: 500;
  color: #1A3A6B;
  text-decoration: none;
}

.forgot-link:hover {
  color: #152E56;
  text-decoration: underline;
}

/* Override Vuetify field styles */
.modern-field >>> .v-input__slot {
  border-radius: 10px !important;
  background: #f8fafc !important;
  border-color: #e2e8f0 !important;
  min-height: 44px !important;
}

.modern-field >>> .v-input__slot:hover {
  border-color: #1A3A6B !important;
}

.modern-field >>> .v-input--is-focused .v-input__slot {
  border-color: #1A3A6B !important;
}

.modern-field >>> .v-label {
  font-size: 13px !important;
  color: #94a3b8 !important;
}

.modern-field >>> .v-icon {
  color: #94a3b8 !important;
  font-size: 18px !important;
}

/* Login Button */
.login-btn {
  background: #1A3A6B !important;
  color: #fff !important;
  border-radius: 10px !important;
  font-size: 14px !important;
  font-weight: 600 !important;
  letter-spacing: 0.01em !important;
  height: 46px !important;
  text-transform: none !important;
  box-shadow: 0 4px 12px rgba(26, 58, 107, 0.3) !important;
  transition: all 0.2s ease !important;
}

.login-btn:hover {
  background: #152E56 !important;
  box-shadow: 0 6px 18px rgba(26, 58, 107, 0.4) !important;
}

.login-btn:active {
  transform: scale(0.98);
}

/* Footer */
.login-footer {
  margin-top: 36px;
  text-align: center;
  font-size: 11px;
  color: #94a3b8;
}
</style>
